import React from 'react';
import { motion } from 'framer-motion';
import { Coffee, MapPin, Clock, Star, Heart, Users, Award, ShoppingCart } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMenu } from '../contexts/MenuContext';
import { useOpeningHours } from '../contexts/OpeningHoursContext';
import Cart from '../components/Cart';

const AboutPage: React.FC = () => {
  const { getCartItemCount } = useMenu();
  const { openingHours } = useOpeningHours();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check if cart should be shown (from URL)
  const showCart = searchParams.get('cart') === 'true';

  // Helper function to format time
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Helper function to capitalize day name
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 pt-24 text-white overflow-hidden">
        {/* Background image */}
        <img 
          src="/banner-background.jpg" 
          alt="About background"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'blur(1px)',
            zIndex: 1
          }}
        />
        
        {/* Dark overlay */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.75)',
            zIndex: 2
          }}
        ></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-bold mb-6">
              <span className="text-gray-200 text-3xl md:text-5xl" style={{ textShadow: '0 4px 6px rgba(0, 0, 0, 0.9), 0 2px 4px rgba(0, 0, 0, 0.9)' }}>About </span><span className="text-red-500 text-4xl md:text-6xl" style={{ textShadow: '0 4px 6px rgba(0, 0, 0, 0.9), 0 2px 4px rgba(0, 0, 0, 0.9)' }}>Kiss Coffee</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
              Your neighborhood coffee haven in the heart of Minehead
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold mb-6 text-gray-900">Our Story</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Kiss Coffee Minehead was born from a simple passion: to bring exceptional coffee 
                and warm hospitality to our community. Located on the charming Friday Street, 
                we've been serving locals and visitors alike with carefully crafted beverages 
                and delicious food.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Every cup tells a story, every meal is made with love, and every visit 
                is an opportunity to create lasting memories. We believe that great coffee 
                brings people together, and that's exactly what we do here at Kiss Coffee.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-lg leading-relaxed">
                  To provide exceptional coffee experiences while fostering community 
                  connections in the heart of Minehead.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              What drives us every day to serve you better
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Passion</h3>
              <p className="text-gray-600 leading-relaxed">
                We're passionate about coffee, food, and creating memorable experiences 
                for every customer who walks through our doors.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Quality</h3>
              <p className="text-gray-600 leading-relaxed">
                We source the finest ingredients and prepare everything with care, 
                ensuring every cup and every bite meets our high standards.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Community</h3>
              <p className="text-gray-600 leading-relaxed">
                We're more than just a coffee shop - we're a gathering place where 
                friends meet, ideas are shared, and memories are made.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location & Hours Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold mb-8 text-gray-900">Visit Us</h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">Address</h3>
                    <p className="text-gray-600 text-lg">
                      16 Friday Street<br />
                      Minehead, TA24 5UA<br />
                      United Kingdom
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Clock className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">Opening Hours</h3>
                    <div className="text-gray-600 text-lg space-y-1">
                      {Object.entries(openingHours).map(([day, hours]) => (
                        <p key={day}>
                          <span className="font-medium">{capitalize(day)}:</span>{' '}
                          {hours.closed ? (
                            <span className="text-red-600">Closed</span>
                          ) : (
                            <span>{formatTime(hours.open)} - {formatTime(hours.close)}</span>
                          )}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl p-8 text-white"
            >
              <h3 className="text-2xl font-bold mb-6">Why Choose Us?</h3>
              <ul className="space-y-4">
                <li className="flex items-center space-x-3">
                  <Star className="h-5 w-5 text-yellow-300" />
                  <span>Premium coffee beans from trusted suppliers</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Star className="h-5 w-5 text-yellow-300" />
                  <span>Fresh, locally sourced ingredients</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Star className="h-5 w-5 text-yellow-300" />
                  <span>Warm, welcoming atmosphere</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Star className="h-5 w-5 text-yellow-300" />
                  <span>Friendly, knowledgeable staff</span>
                </li>
                <li className="flex items-center space-x-3">
                  <Star className="h-5 w-5 text-yellow-300" />
                  <span>Convenient location in Minehead</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-4">Come Experience the Difference</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Whether you're a coffee connoisseur or just looking for a cozy place to relax, 
              we'd love to welcome you to Kiss Coffee Minehead.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/menu"
                className="bg-white text-red-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                View Our Menu
              </a>
              <a
                href="/order"
                className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-red-700 transition-colors"
              >
                Order Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cart Button - Only show when cart has items */}
      {getCartItemCount() > 0 && (
        <div className="fixed top-20 sm:top-20 right-4 z-40">
          <button
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('cart', 'true');
              setSearchParams(newParams);
            }}
            className="group bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-xl inline-flex items-center relative text-sm shadow-lg"
          >
            <ShoppingCart className="h-4 w-4 mr-2 group-hover:rotate-12 transition-transform" />
            <span className="hidden sm:inline">View Cart</span>
            <span className="sm:hidden">Cart</span>
            <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-md border border-red-600">
              {getCartItemCount()}
            </span>
          </button>
        </div>
      )}

      {/* Cart Modal */}
      {showCart && (
        <Cart onClose={() => navigate(-1)} />
      )}
    </div>
  );
};

export default AboutPage;




